import styled, { keyframes } from "styled-components";
import { Flex, Text } from "rebass/styled-components";

const livePulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(255, 68, 68, 0.55);
  }
  70% {
    box-shadow: 0 0 0 5px rgba(255, 68, 68, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(255, 68, 68, 0);
  }
`;

export const CardContainer = styled.article<{ sponsored?: boolean }>`
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.colors.darkerNavy};
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid
    ${({ sponsored }) =>
      sponsored ? "rgba(255, 215, 0, 0.4)" : "rgba(143, 169, 175, 0.06)"};
  box-shadow: ${({ sponsored }) =>
    sponsored
      ? "0 0 0 1px rgba(255, 215, 0, 0.1), 0 4px 14px rgba(0, 0, 0, 0.3)"
      : "none"};
  cursor: pointer;
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease,
    border-color 0.2s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: ${({ sponsored }) =>
      sponsored
        ? "0 0 0 1px rgba(255, 215, 0, 0.25), 0 12px 32px rgba(0, 0, 0, 0.5)"
        : "0 12px 28px rgba(0, 0, 0, 0.4)"};
    border-color: ${({ sponsored }) =>
      sponsored ? "rgba(255, 215, 0, 0.55)" : "rgba(143, 169, 175, 0.18)"};
  }
`;

export const BannerWrapper = styled.div`
  position: relative;
  aspect-ratio: 1140 / 470;
  background: ${({ theme }) => theme.colors.navy500};
  overflow: hidden;
`;

export const BannerImage = styled.img<{ ended?: boolean }>`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  filter: ${({ ended }) => (ended ? "grayscale(1) brightness(0.55)" : "none")};
`;

export const EndedOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(12, 19, 24, 0.5);
`;

export const EndedLabel = styled.span`
  transform: rotate(-8deg);
  padding: 6px 14px;
  border: 2px solid rgba(255, 255, 255, 0.85);
  border-radius: 6px;
  background: rgba(12, 19, 24, 0.55);
  color: ${({ theme }) => theme.colors.ice};
  font-size: 13px;
  font-weight: 800;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  white-space: nowrap;
`;

export const BannerScrim = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    180deg,
    rgba(12, 19, 24, 0) 55%,
    rgba(12, 19, 24, 0.6) 100%
  );
`;

export const StatusPill = styled.span<{ accent: string }>`
  position: absolute;
  bottom: 10px;
  left: 10px;
  z-index: 2;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(12, 19, 24, 0.85);
  backdrop-filter: blur(8px);
  border-radius: 999px;
  padding: 4px 10px;
  font-size: 10.5px;
  font-weight: 700;
  color: ${({ accent }) => accent};
  white-space: nowrap;
  letter-spacing: 0.06em;
`;

export const LiveDot = styled.span`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.heartRed ?? "#ff4444"};
  flex-shrink: 0;
  animation: ${livePulse} 1.8s ease-out infinite;
`;

export const CardBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px 16px 16px;
  flex: 1;
`;

export const StatsRow = styled(Flex)`
  align-items: center;
  flex-wrap: wrap;
  gap: 6px 16px;
`;

export const Stat = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.chalk};
  white-space: nowrap;

  svg {
    color: ${({ theme }) => theme.colors.envy};
    flex-shrink: 0;
  }
`;

export const StatNum = styled.span`
  color: ${({ theme }) => theme.colors.ice};
  font-weight: 800;
`;

export const Title = styled(Text)`
  color: ${({ theme }) => theme.colors.ice};
  font-size: 17px;
  font-weight: 700;
  line-height: 1.25;
  letter-spacing: -0.01em;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const PrizeRow = styled.div<{ sponsored?: boolean }>`
  display: flex;
  align-items: center;
  gap: 7px;
  min-width: 0;

  svg {
    color: ${({ sponsored, theme }) =>
      sponsored ? theme.colors.goldStar : theme.colors.envy};
    flex-shrink: 0;
  }
`;

export const Footer = styled(Flex)`
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 2px;
  padding-top: 12px;
  border-top: 1px solid rgba(143, 169, 175, 0.1);
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 12.5px;
`;

export const PrizeAmount = styled.span<{ sponsored?: boolean }>`
  font-size: 13px;
  font-weight: 700;
  color: ${({ sponsored, theme }) =>
    sponsored ? theme.colors.goldStar : theme.colors.envy};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
`;

export const TimeText = styled.span<{ ending?: boolean }>`
  color: ${({ ending, theme }) =>
    ending ? theme.colors.caution : theme.colors.chalk};
  font-weight: 600;
  white-space: nowrap;
`;

export const FooterCta = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-left: auto;
  font-size: 12px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.envy};
  white-space: nowrap;
  flex-shrink: 0;
`;
